import { useEffect } from 'react';
import {
  Grid,
  Typography,
  Modal,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getQuantityOptions } from './dropdownOptions.js';

export default function GamesSelectModal({
  isOpen,
  handleClose,
  setForm,
  games,
  jurisdiction,
  bgColor,
  accentColor,
  modalTitle,
}) {
  const { t } = useTranslation();
  const quantityOptions = getQuantityOptions(t);

  useEffect(() => {
    const fetchGames = async () => {
      if (games && games.length > 0) {
        return;
      }

      try {
        const res = await axios.post(
          process.env.REACT_APP_ENDPOINT_RSS_FETCH_GAMES,
          { jurisdiction },
        );
        const gamesData = res.data;
        const updatedGames = gamesData.map(game => ({
          ...game,
          quantity: '',
        }));
        setForm(prevForm => ({
          ...prevForm,
          games: updatedGames,
        }));
      } catch (error) {
        console.error('Error fetching games:', error);
      }
    };

    if (isOpen) {
      fetchGames();
    }
  }, [isOpen, setForm, games, jurisdiction]);

  const handleChange = (e, gameId) => {
    const { value } = e.target;
    setForm(prevForm => ({
      ...prevForm,
      games: prevForm.games.map(game =>
        game.gameId === gameId ? { ...game, quantity: value } : game,
      ),
    }));
  };

  // API returns games sorted DESC by gameId
  // Distribute games horizontally: even indices go to left column, odd to right
  const leftColumnGames = games.filter((_, index) => index % 2 === 0);
  const rightColumnGames = games.filter((_, index) => index % 2 === 1);

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Grid
        container
        spacing={2}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '80%', md: '70%' },
          maxHeight: { xs: '80vh', sm: '80vh', md: '80vh' },
          overflowY: 'auto',
          bgcolor: bgColor,
          p: 4,
        }}
      >
        <Grid
          size={12}
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Typography
            id='modal-modal-title'
            sx={{ fontSize: '25px', color: '#FFFFFF' }}
          >
            {modalTitle}
          </Typography>
        </Grid>
        <Grid size={6}>
          {leftColumnGames.map(game => (
            <FormControl
              key={game.gameId}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            >
              <InputLabel>{game.display}</InputLabel>
              <Select
                value={game.quantity || ''}
                label={`${game.display}`}
                onChange={e => handleChange(e, game.gameId)}
                name={game.gameId}
              >
                {quantityOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </Grid>
        <Grid size={6}>
          {rightColumnGames.map(game => (
            <FormControl
              key={game.gameId}
              sx={{
                backgroundColor: '#ffffff',
                marginBottom: '15px',
                width: '100%',
              }}
            >
              <InputLabel>{game.display}</InputLabel>
              <Select
                value={game.quantity || ''}
                label={`${game.display}`}
                onChange={e => handleChange(e, game.gameId)}
                name={game.gameId}
              >
                {quantityOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
        </Grid>
        <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='contained'
            sx={{ backgroundColor: accentColor, color: '#000000' }}
            onClick={handleClose}
          >
            {t('buttons.done')}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
}
